import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Camera, X, Image as ImageIcon, Plus, Video } from 'lucide-react';
import toast from 'react-hot-toast';
import { BASE_URL } from '@/lib/API';

export const SiteVisitUploadPhotos = ({
  photos = [],
  onPhotosChange,
  existingPhotos = [],
  expandedSection,
  onToggle,
  title = "02. UPLOAD SITE PHOTOS"
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);

  const isExpanded = typeof expandedSection !== 'undefined'
    ? expandedSection === 'upload_photos'
    : internalExpanded;

  const handleToggle = () => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalExpanded(!internalExpanded);
    }
  };

  const handlePhotoUpload = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      const validFiles = newFiles.filter(file => {
        if (file.size > 20 * 1024 * 1024) {
          toast.error(`File ${file.name} exceeds 20MB limit.`);
          return false;
        }
        return true;
      });

      const formattedPhotos = validFiles.map(file => ({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadId: `sitephoto_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
      }));

      if (onPhotosChange) {
        onPhotosChange([...photos, ...formattedPhotos]);
      }
    }
    e.target.value = null;
  };

  const removePhoto = (idx) => {
    if (onPhotosChange) {
      onPhotosChange(photos.filter((_, i) => i !== idx));
    }
  };

  const totalCount = photos.length + (existingPhotos?.length || 0);

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white mb-2 shadow-sm">
      <div 
        className="px-3 py-2.5 bg-slate-50/80 cursor-pointer flex justify-between items-center transition-colors hover:bg-slate-100/80"
        onClick={handleToggle}
      >
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">
            {title}
          </span>
          {totalCount > 0 && (
            <span className="bg-slate-900 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">
              {totalCount}
            </span>
          )}
        </div>
        {isExpanded ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
      </div>

      {isExpanded && (
        <div className="p-3 border-t border-slate-200 space-y-3 bg-white">
          <div className="flex items-center justify-end border-b border-slate-100 pb-2">
            {/* <span className="text-xs text-slate-500 font-medium">
              Upload general site photos, site condition pictures, or walk-through videos.
            </span> */}
            <label className="flex items-center gap-1.5 text-[11px] font-bold bg-slate-900 text-white px-3 py-1.5 rounded-md hover:bg-black transition-colors shadow-sm cursor-pointer shrink-0">
              <Camera size={14} /> Upload Photos
              <input 
                type="file" 
                className="hidden" 
                accept="image/*,video/*" 
                multiple 
                onChange={handlePhotoUpload} 
              />
            </label>
          </div>

          {photos.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                New Photos ({photos.length})
              </h5>
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {photos.map((item, idx) => (
                  <div 
                    key={idx} 
                    className="group relative aspect-square rounded-lg border border-slate-200 overflow-hidden bg-slate-50 shadow-sm"
                  >
                    {item.preview ? (
                      <img 
                        src={item.preview} 
                        alt={item.name} 
                        className="w-full h-full object-cover" 
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center p-2 text-center">
                        <Video size={20} className="text-slate-400 mb-1" />
                        <span className="text-[10px] font-semibold text-slate-600 truncate w-full">
                          {item.name}
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-start justify-end p-1">
                      <button
                        type="button"
                        onClick={() => removePhoto(idx)}
                        className="bg-white/90 hover:bg-red-500 hover:text-white text-slate-700 rounded-full p-1 transition-colors"
                        title="Remove photo"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {existingPhotos && existingPhotos.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <h5 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                Existing Site Photos ({existingPhotos.length})
              </h5>
              <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-3">
                {existingPhotos.map((photo, idx) => {
                  const fileId = photo.fileId || photo._id;
                  const imageUrl = fileId ? `${BASE_URL}/api/verve-leads/site-visit/media/${fileId}` : null;
                  return (
                    <div 
                      key={idx} 
                      className="aspect-square rounded-lg border border-slate-200 overflow-hidden bg-slate-50 relative group shadow-sm"
                    >
                      {imageUrl ? (
                        <img 
                          src={imageUrl} 
                          alt="Existing Site Photo" 
                          className="w-full h-full object-cover" 
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon className="w-6 h-6 text-slate-400" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {photos.length === 0 && (!existingPhotos || existingPhotos.length === 0) && (
            <div className="text-center py-6 bg-slate-50/50 border border-dashed border-slate-200 rounded-md">
              <Camera className="w-8 h-8 text-slate-300 mx-auto mb-1" />
              <p className="text-xs text-slate-500 font-medium">No site visit photos uploaded yet.</p>
              <p className="text-[11px] text-slate-400">Click &quot;Upload Photos&quot; to add site condition images.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SiteVisitUploadPhotos;
